import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  Alert,
  AlertTitle,
  AlertDescription,
  Flex,
  Box,
} from '@chakra-ui/react';
import FileUpload from '../fileUploader/FileUpload';
import MySuccessIcon from '../../assets/img/profile/successIcon.svg';

const DocumentUploadModal = ({ isOpen, onClose, onUpload }) => {
  const [files, setFiles] = useState({
    company_doc: [],
    tax_doc: []
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleFileChange = (newFiles, documentType) => {
    const maxFiles = documentType === 'company_doc' ? 3 : 1;
    
    // Check if adding new files would exceed the limit
    const totalFiles = files[documentType].length + newFiles.length;
    if (totalFiles > maxFiles) {
      alert(`You can only upload a maximum of ${maxFiles} ${documentType === 'company_doc' ? 'company documents' : 'tax document'}`);
      return;
    }
    
    setFiles(prev => ({
      ...prev,
      [documentType]: [...prev[documentType], ...newFiles]
    }));
    
    // Dispatch custom event to update file display
    window.dispatchEvent(new CustomEvent('fileListUpdated', {
      detail: {
        documentType,
        files: [...files[documentType], ...newFiles]
      }
    }));
  };

  const handleRemoveFile = (documentType, indexToRemove) => {
    setFiles(prev => {
      const newFiles = {
        ...prev,
        [documentType]: prev[documentType].filter((_, index) => index !== indexToRemove)
      };
      
      // Dispatch custom event to update file display
      window.dispatchEvent(new CustomEvent('fileListUpdated', {
        detail: {
          documentType,
          files: newFiles[documentType]
        }
      }));
      
      return newFiles;
    });
  };

  const renderFileList = (fileArray, documentType) => {
    if (!fileArray.length) return null;

    return (
      <VStack align="stretch" mt={2} spacing={2}>
        {fileArray.map((file, index) => (
          <Flex
            key={index}
            p={2}
            bg="gray.50"
            borderRadius="md"
            align="center"
            justify="space-between"
          >
            <Text fontSize="sm" noOfLines={1}>
              {file.name} ({(file.size / 1024).toFixed(2)} KB)
            </Text>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={() => handleRemoveFile(documentType, index)}
            >
              Remove
            </Button>
          </Flex>
        ))}
      </VStack>
    );
  };

  const handleUpload = async () => {
    try {
      const formData = new FormData();
      const documentTypes = [];
      
      files.company_doc.forEach(file => {
        formData.append('files', file);
        documentTypes.push('company_doc');
      });

      files.tax_doc.forEach(file => {
        formData.append('files', file);
        documentTypes.push('tax_doc');
      });

      formData.append('documentType', JSON.stringify(documentTypes));

      await onUpload(formData);
      setShowSuccess(true);
      setFiles({ company_doc: [], tax_doc: [] });

      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 5000);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      closeOnOverlayClick={false} 
      isCentered 
      size="xl"
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent maxH="90vh">
        <Box display="flex" flexDirection="column" maxH="90vh">
          {!showSuccess && (
            <ModalHeader textAlign='center' p="2rem" pb="1rem" fontSize="2xl">
              Upload Documents
            </ModalHeader>
          )}
          
          <ModalBody 
            p="2rem" 
            pt={!showSuccess ? "0" : "2rem"}
            flex="1"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#E2E8F0',
                borderRadius: '24px',
              },
            }}
          >
            {showSuccess ? (
              <Alert
                status="success"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                bg="transparent"
                py={8}
              >
                <Box mb={6}>
                  <img src={MySuccessIcon} alt="Success Icon" width="40px" />
                </Box>
                <AlertTitle mb={4} fontSize="2xl">
                  Success
                </AlertTitle>
                <AlertDescription>
                  We have successfully created your new account. You will get notified once admin approves.
                </AlertDescription>
                <Button 
                  colorScheme="purple" 
                  onClick={onClose}
                  mt={6}
                  width="102px"
                  height="48px"
                  borderRadius="6px"
                >
                  Okay
                </Button>
              </Alert>
            ) : (
              <VStack spacing={6} align="stretch">
                <Text>Please provide following documents</Text>
                <Box>
                  <FileUpload 
                    onFileChange={handleFileChange} 
                    buttonText="Choose File" 
                    label="Company Document" 
                    placeholder="No file Chosen" 
                    helperText="Upload a maximum of 3 documents."
                    documentType="company_doc"
                  />
                  {renderFileList(files.company_doc, 'company_doc')}
                </Box>
                
                <Box>
                  <FileUpload 
                    onFileChange={handleFileChange} 
                    buttonText="Choose File" 
                    label="Tax Details" 
                    placeholder="No file Chosen" 
                    helperText="Upload 1 tax document."
                    documentType="tax_doc"
                  />
                  {renderFileList(files.tax_doc, 'tax_doc')}
                </Box>
              </VStack>
            )}
          </ModalBody>

          {!showSuccess && (
            <Box p="2rem" pt="0">
              <Button 
                colorScheme="purple" 
                onClick={handleUpload} 
                width="100%" 
                height="48px" 
                borderRadius="6px"
                isDisabled={!files.company_doc.length && !files.tax_doc.length}
              >
                Upload
              </Button>
            </Box>
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default DocumentUploadModal;
